import { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { playSound, SOUNDS } from '../utils/soundEffects';

export default function WaitingRoomQueue({ meetingId, isHost }) {
  const [waitingParticipants, setWaitingParticipants] = useState([]);
  const [processingIds, setProcessingIds] = useState(new Set());

  useEffect(() => {
    if (!isHost || !meetingId) return;

    const waitingRef = collection(db, 'meetings', meetingId, 'waitingRoom');
    const q = query(waitingRef, where('status', '==', 'waiting'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const participants = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Play sound when new participant joins waiting room
      if (participants.length > waitingParticipants.length) {
        playSound(SOUNDS.WAITING_ROOM);
      }

      setWaitingParticipants(participants);
    }, (error) => {
      // Gracefully handle permission errors - waiting room will be empty but app continues
      if (error.code === 'permission-denied') {
        console.warn('Unable to access waiting room - check Firestore rules deployment');
        setWaitingParticipants([]);
      } else {
        console.error('Error listening to waiting room:', error);
      }
    });

    return () => unsubscribe();
  }, [meetingId, isHost]);

  const admitParticipant = async (userId) => {
    if (processingIds.has(userId)) return;

    setProcessingIds(prev => new Set([...prev, userId]));
    try {
      const waitingRef = doc(db, 'meetings', meetingId, 'waitingRoom', userId);
      await setDoc(waitingRef, { status: 'admitted' }, { merge: true });
      // The participant's listener will detect this change and join the meeting
      // Clean up after a longer delay to ensure they received the update
      setTimeout(async () => {
        try {
          await deleteDoc(waitingRef);
        } catch (err) {
          console.error('Error deleting admitted participant:', err);
        }
        setProcessingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      }, 5000); // Increased from 2000ms to 5000ms
    } catch (error) {
      console.error('Error admitting participant:', error);
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const rejectParticipant = async (userId) => {
    if (processingIds.has(userId)) return;

    setProcessingIds(prev => new Set([...prev, userId]));
    try {
      const waitingRef = doc(db, 'meetings', meetingId, 'waitingRoom', userId);
      await setDoc(waitingRef, { status: 'rejected' }, { merge: true });
      // Delete after a delay to ensure they see the rejection
      setTimeout(async () => {
        try {
          await deleteDoc(waitingRef);
        } catch (err) {
          console.error('Error deleting rejected participant:', err);
        }
        setProcessingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      }, 3000); // Increased from 1000ms to 3000ms
    } catch (error) {
      console.error('Error rejecting participant:', error);
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const admitAll = async () => {
    const allIds = waitingParticipants.map(p => p.userId);
    allIds.forEach(userId => admitParticipant(userId));
  };

  if (!isHost || waitingParticipants.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 bg-[#1c1c1e] border border-white/10 rounded-xl shadow-2xl p-4 max-w-sm z-[60] animate-fade-in">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Waiting Room ({waitingParticipants.length})
        </h3>
        {waitingParticipants.length > 1 && (
          <button
            onClick={admitAll}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            Admit All
          </button>
        )}
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
        {waitingParticipants.map(participant => {
          const isProcessing = processingIds.has(participant.userId);

          return (
            <div
              key={participant.userId}
              className="flex items-center justify-between bg-[#2c2c2e] p-3 rounded-lg border border-white/5 hover:border-white/10 transition-all"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                  {participant.userName?.[0]?.toUpperCase() || 'G'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">
                    {participant.userName || 'Guest'}
                  </p>
                  <p className="text-gray-400 text-xs truncate">
                    {participant.userEmail || 'No email'}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 flex-shrink-0 ml-2">
                <button
                  onClick={() => admitParticipant(participant.userId)}
                  disabled={isProcessing}
                  className="bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-white px-3 py-1.5 rounded text-xs font-medium transition-all disabled:cursor-not-allowed"
                  title="Admit to meeting"
                >
                  {isProcessing ? '...' : 'Admit'}
                </button>
                <button
                  onClick={() => rejectParticipant(participant.userId)}
                  disabled={isProcessing}
                  className="bg-red-500 hover:bg-red-600 disabled:bg-red-500/50 text-white px-3 py-1.5 rounded text-xs font-medium transition-all disabled:cursor-not-allowed"
                  title="Reject entry"
                >
                  {isProcessing ? '...' : 'Reject'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
